import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useRef } from "react";
import Modal, { setSelectedModal } from "../../Modal";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { debounce, isEmpty } from "lodash";
import classNames from "classnames";
import {
  EMPTY_ARRAY,
  EMPTY_STRING,
  MODAL_SIZE,
  MOUSE_EVENTS,
  SEARCH_DEBOUNCE_TIME,
  DATAMODEL_CREATION,
  ONE,
  ZERO,
  ALERT_MESSAGES,
} from "../../../../constants";
import { selectBackendTemplates } from "../../../../pages/Applications";
import {
  createCollectionFromTemplates,
  fetchDataModel,
  fetchFilteredDataModels,
  selectDataModels,
  selectSelectedDataModel,
  selectTotalBackgroundTasks,
  selectTotalCollections,
  selectTotalEndpoints,
  selectTotalServices,
  setDataModelResources,
  setDataModelWithPendingResources,
  selectAreResourcesPending,
} from "../../../../pages/DataModels";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { setSelectedDrawer } from "../../Drawer";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { errorIcon, exclamationIcon } from "../../../../assets/icons";
import { selectAlert } from "../../Alert";

const TIME_INTERVAL = 20000;
const { LG } = MODAL_SIZE;
const { ADD_RESOURCES } = SPINNER_NAMES;
const { WARNING_MESSAGE } = ALERT_MESSAGES;

const ImportFromDataModal = () => {
  const dispatch = useDispatch();
  const fieldRef = useRef();

  const dataModels = useSelector(selectDataModels);
  const predefinedTemplates = useSelector(selectBackendTemplates);
  const dataModel = useSelector(selectSelectedDataModel);
  const selectedError = useSelector(selectAlert);
  const areResourcesPending = useSelector(selectAreResourcesPending);
  const totalBackgroundTasks = useSelector(selectTotalBackgroundTasks);
  const totalCollections = useSelector(selectTotalCollections);
  const totalEndpoints = useSelector(selectTotalEndpoints);
  const totalServices = useSelector(selectTotalServices);

  const [templates, setTemplates] = useState(EMPTY_ARRAY);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDataModel, setSelectedDataModel] = useState(EMPTY_STRING);
  const [selectedTemplate, setSelectedTemplate] = useState(EMPTY_STRING);
  const [areResourcesAdding, setAreResourcesAdding] =
    useState(areResourcesPending);
  const [filteredDataModels, setFilteredDataModels] = useState(dataModels.data);
  const [searchTerm, setSearchedTerm] = useState(EMPTY_STRING);
  const [showWarning, setShowWarning] = useState(false);
  const [error, setError] = useState(false);
  const [messages, setMessages] = useState(EMPTY_ARRAY);
  const [currentIndex, setCurrentIndex] = useState(ZERO);

  const initialValues = {
    template: EMPTY_STRING,
    datamodel: EMPTY_STRING,
  };

  const validationSchema = Yup.object({
    datamodel: Yup.string(),
    template: Yup.string().when("datamodel", {
      is: (datamodel) => datamodel && datamodel !== EMPTY_STRING,
      then: Yup.string().oneOf(
        ["fromScratch"],
        'Template must be "From Scratch" when data model is selected'
      ),
      otherwise: Yup.string(),
    }),
  });

  useEffect(() => {
    selectedError && setError(selectedError.message || selectedError);
  }, [selectedError]);

  useEffect(()=> { 
    setFilteredDataModels(dataModels.data.filter(({name}) => name !== dataModel.name));
  },[dataModels, dataModel]);

  useEffect(() => {
    setMessages(DATAMODEL_CREATION);
  }, []);

  useEffect(() => {
    const areResouresAlreadyExists =
      totalBackgroundTasks ||
      totalEndpoints ||
      totalServices ||
      totalCollections;

    setShowWarning(!!areResouresAlreadyExists);
  }, [totalBackgroundTasks, totalEndpoints, totalServices, totalCollections]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (currentIndex < messages.length - ONE)
        setCurrentIndex(currentIndex + ONE);
    }, TIME_INTERVAL);

    return () => {
      clearInterval(timer);
    };
  }, [currentIndex, messages]);

  useEffect(() => {
    if (!areResourcesPending && areResourcesAdding)
      dispatch(setSelectedModal());
  }, [areResourcesPending, areResourcesAdding, dispatch]);

  useEffect(() => {
    if (predefinedTemplates) setTemplates(predefinedTemplates);
  }, [predefinedTemplates]);

  useEffect(() => {
    if (dataModel?.parentDataModelName) 
      setSearchedTerm(dataModel.parentDataModelName);
  }, [dataModel]);

  useEffect(() => {
    if (dataModel?.templateType)
      setSelectedTemplate(dataModel.templateType);
  }, [dataModel]);

  const handleOnSearch = debounce(
    (name) =>
      dispatch(fetchFilteredDataModels({ name })).then((res) => {
        setFilteredDataModels(res.payload.data.filter(({ name }) => name !== dataModel.name));
      }),
    SEARCH_DEBOUNCE_TIME
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fieldRef.current && !fieldRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener(MOUSE_EVENTS.MOUSE_DOWN, handleClickOutside);

    return () => {
      document.removeEventListener(MOUSE_EVENTS.MOUSE_DOWN, handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setShowWarning(selectedDataModel || selectedTemplate);
  }, [selectedDataModel, selectedTemplate]);

  const handleInputChange = (e, setFieldValue) => {
    setSearchedTerm(e.target.value);
    if (e.target.value === EMPTY_STRING) {
      setSelectedDataModel(EMPTY_STRING);
      setFieldValue("datamodel", EMPTY_STRING);
    }
    setIsOpen(true);
    handleOnSearch(e.target.value);
  };

  const handleOptionClick = ({ name, setFieldValue }) => {
    setSelectedDataModel(name);
    setSearchedTerm(name);
    setFieldValue("datamodel", name);
    setSelectedTemplate(EMPTY_STRING);
    setFieldValue("template", EMPTY_STRING);
  };

  const handleSelection = (e, setFieldValue) => {
    setSelectedTemplate(e.target.value)
    if (e.target.value !== EMPTY_STRING) {
      setFieldValue("datamodel", EMPTY_STRING);
      setSelectedDataModel(EMPTY_STRING);
      setSearchedTerm(EMPTY_STRING);
    }
  };

  const handleOnSubmit = (values) => {
    const { template, datamodel } = values;
    const requestBody = {
      dataModelId: dataModel.id,
      dataModel: datamodel,
      template,
    };
    dispatch(createCollectionFromTemplates(requestBody)).then((res) => {
      if (!res?.payload) return;
      dispatch(fetchDataModel(dataModel.id)).then((res) => {
        if (!res?.payload) return;
        dispatch(setDataModelResources(res.payload.dataModelResources));
        dispatch(setDataModelWithPendingResources(res.payload));
        setAreResourcesAdding(true);
        dispatch(setSelectedDrawer());
      });
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({
        values,
        handleSubmit,
        handleChange,
        setFieldValue,
        errors,
        touched,
      }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary-light mr-2"
              type="button"
              disabled={areResourcesAdding}
              onClick={() => dispatch(setSelectedModal())}
            >
              Cancel
            </button>
            {!areResourcesAdding && (
              <button
                className="btn btn-primary mr-2 disabled:cursor-not-allowed"
                type="Submit"
                disabled={showWarning}
              >
                <Spinner name={ADD_RESOURCES} persistSize>
                  Submit
                </Spinner>
              </button>
            )}
          </div>
        );

        return (
          <Form autoComplete="off" onSubmit={handleSubmit}>
            <Modal
              title="Create Collection"
              size={LG}
              footer={footer}
              handleClose={() => dispatch(setSelectedModal())}
            >
              {!areResourcesAdding ? (
                <div className="p-4">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Templates
                    </label>
                    <Field
                      as="select"
                      name="template"
                      className="form-control"
                      value={selectedTemplate}
                      onChange={(e) => {
                        handleChange(e);
                        handleSelection(e, setFieldValue);
                      }}
                    >
                      <option hidden> Select Template </option>
                      {templates.map((each) => (
                        <option value={each.value} key={each.name}>
                          {each.name}
                        </option>
                      ))}
                    </Field>
                  </div>
                  <div className="text-base text-gray-200 m-2 text-center">
                    OR
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Datamodel
                    </label>
                    <div
                      className="form-control"
                      onClick={() => setIsOpen(!isOpen)}
                      tabIndex={0}
                    >
                      <input
                        type="text"
                        onChange={(e) => handleInputChange(e, setFieldValue)}
                        placeholder="Select Datamodel"
                        className="form-control border-2"
                        name="datamodel"
                        value={searchTerm}
                      />
                      {isOpen && (
                        <div
                          className=" form-control mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 p-1 space-y-1 overflow-y-scroll no-scrollbar max-h-40
                        "
                          ref={fieldRef}
                        >
                          {isEmpty(filteredDataModels) ? (
                            <div className="no-options">No options found</div>
                          ) : (
                            filteredDataModels.map(({ name }) => (
                              <div
                                key={name}
                                className={classNames(
                                  "cursor-pointer hover:bg-blue-200 rounded-sm",
                                  {
                                    "bg-blue-500 text-white":
                                      name === selectedDataModel,
                                  }
                                )}
                                onClick={() =>
                                  handleOptionClick({ name, setFieldValue })
                                }
                              >
                                {name}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-30 flex-col justify-center mt-6 mb-6">
                  <div>
                    <div className="flex justify-center">
                      <img
                        src={
                          error?.customErrorMessage || error
                            ? errorIcon
                            : messages[currentIndex]?.image
                        }
                        className={classNames("w-12 h-12 justify-center", {
                          "animate-bounce": isEmpty(error),
                        })}
                        alt="process-icon"
                      />
                    </div>

                    <div
                      className={classNames(
                        "text-lg justify-center text-center font-bold",
                        { "!text-red-600 mt-2": !isEmpty(error) },
                        { "text-white animate-pulse": isEmpty(error) }
                      )}
                    >
                      {!isEmpty(error)
                        ? `Error : ${error?.customErrorMessage || error}`
                        : messages[currentIndex]?.message}
                    </div>
                  </div>
                </div>
              )}
              {showWarning && !areResourcesAdding && (
                <div className="flex flex-col bg-yellow-100 p-2 my-2 mx-4 rounded-md mb-4 border-l-4 border-yellow-400">
                  <div className="flex items-center">
                    <div className="icon mr-3">
                      <img
                        src={exclamationIcon}
                        alt="exclamation-icon"
                        className="h-10 w-16"
                      />
                    </div>
                    <div
                      className={classNames(
                        "text-sm font-medium text-yellow-600"
                      )}
                    >
                      {WARNING_MESSAGE}
                    </div>
                  </div>
                  <div className="ml-auto">
                    <button
                      className="font-bold bg-yellow-400 text-xs cursor-pointer rounded-md p-2 hover:text-yellow-800 mr-2"
                      onClick={() => setShowWarning(false)}
                    >
                      Agree
                    </button>
                  </div>
                </div>
              )}
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ImportFromDataModal;
